// api-groups.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// API Models based on your GET.txt specification
export interface ApiGroupResponse {
  id: number;
  name: string;
  code: string;
  policyBaseline: {
    id: number;
    name: string;
    description: string;
    targetType: string;
    scopes: string[];
    status: string;
    createdDate: string;
  } | null;
  rateLimit: string | null;
  upstreamTimeout: string | null;
  pathHandling: string | null;
  routeCount: number;
}

export interface ApiGroupRequest {
  name: string;
  policyId: number;
  rateLimit: string;
  upstreamTimeout: string;
  pathHandling: string;
}

export interface PolicyBaseline {
  id: number;
  name: string;
  description: string;
  targetType: string;
  scopes: string[];
  status: string;
  createdDate: string;
}

// Policy dropdown item from /admin/access-policies/dropdown
export interface PolicyDropdownItem {
  id: number;
  name: string;
  description: string | null;
  targetType: string;
  scopes: string[];
  status?: string;
  createdDate?: string;
}

// Display model for the UI
export interface ApiGroupDisplay {
  id?: number;
  name: string;
  code?: string;
  type: 'Dynamic' | 'Static' | 'Synced' | 'Business domain' | 'Backend system' | 'Platform';
  members: string;
  rule: string;
  scope: string[];
  policy: string;
  policyId?: number;
  rateLimit?: string;
  timeout?: string;
  pathHandling?: string;
  routeCount?: number;
  policyBaseline?: {
    id: number;
    name: string;
    description: string;
    targetType: string;
    scopes: string[];
    status: string;
    createdDate: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ApiGroupsService {
  private baseUrl = environment.baseUrl;

  constructor(private http: HttpClient) {}

  // GET /admin/api-groups - List all API groups
  getApiGroups(): Observable<ApiGroupResponse[]> {
    return this.http.get<ApiGroupResponse[]>(
      `${this.baseUrl}/admin/api-groups`
    );
  }

  // GET /admin/api-groups/{id} - Get a single API group
  getApiGroup(id: number): Observable<ApiGroupResponse> {
    return this.http.get<ApiGroupResponse>(
      `${this.baseUrl}/admin/api-groups/${id}`
    );
  }

  // POST /admin/api-groups - Create a new API group
  createApiGroup(payload: ApiGroupRequest): Observable<ApiGroupResponse> {
    return this.http.post<ApiGroupResponse>(
      `${this.baseUrl}/admin/api-groups`,
      payload
    );
  }

  // PUT /admin/api-groups/{id} - Update an API group
  updateApiGroup(id: number, payload: ApiGroupRequest): Observable<ApiGroupResponse> {
    return this.http.put<ApiGroupResponse>(
      `${this.baseUrl}/admin/api-groups/${id}`,
      payload
    );
  }

  // GET /admin/access-policies/dropdown - Get policies for dropdown
  getPoliciesDropdown(): Observable<PolicyDropdownItem[]> {
    return this.http.get<PolicyDropdownItem[]>(
      `${this.baseUrl}/admin/access-policies/dropdown`
    );
  }

  // Get available policy baselines (legacy - kept for compatibility)
  getPolicies(): Observable<PolicyBaseline[]> {
    return this.http.get<PolicyBaseline[]>(
      `${this.baseUrl}/admin/policies`
    );
  }
}